import { Box, Typography } from '@mui/material';

import {
  MarketingCtaBand,
  MarketingFeatureGrid,
  MarketingHero,
} from '@/components/Marketing/blocks';
import { MarketingPageShell } from '@/components/Marketing/MarketingPageShell';
import { usePageMeta } from '@/hooks/usePageMeta';

export default function FixAndFlipPage() {
  usePageMeta(
    'Unbeatable Fix and Flip',
    'Short-term financing for fix-and-flip and rehab projects for real estate operators.'
  );
  return (
    <MarketingPageShell
      title="Unbeatable Fix and Flip"
      subtitle="Short-term financing for acquisition and renovation—built for operators."
    >
      <MarketingHero
        eyebrow="Meet the Unbeatable Fix and Flip"
        title="Flip for more ROI."
        description="Fix-and-flip loans for business-purpose acquisitions and rehabs. We focus on the project, not stacks of personal income docs."
      />

      <Typography variant="body1" sx={{ mb: 3, lineHeight: 1.8 }}>
        Unbeatable Fix and Flip mortgages are designed for real estate operators
        nationwide. Qualification leans on the project: purchase, renovation
        budget, and after-repair value. Up to 90% purchase leverage on eligible
        programs.
      </Typography>

      <MarketingFeatureGrid
        items={[
          {
            title: 'Qualification based on the project',
            body: 'ARV, budget, and experience drive the box.',
          },
          {
            title: 'No personal income verification required',
            body: 'Investor-friendly documentation on approved programs.',
          },
          {
            title: 'Up to 90% purchase leverage',
            body: 'Preserve capital for construction and carrying costs.',
          },
        ]}
      />

      <Typography variant="h5" sx={{ fontWeight: 800, mt: 4, mb: 2 }}>
        Perfect for
      </Typography>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 4 }}>
        {[
          'Real estate operators',
          'Investors',
          'Fix to rent',
          'Short-term investment',
          'Light rehab',
          'Wholetail',
          'Wholesale',
        ].map((label) => (
          <Box
            key={label}
            sx={{
              px: 2,
              py: 0.75,
              borderRadius: 999,
              bgcolor: 'action.hover',
              typography: 'body2',
              fontWeight: 600,
            }}
          >
            {label}
          </Box>
        ))}
      </Box>

      <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
        Investors don't use buyers agents
      </Typography>
      <Typography
        variant="body2"
        color="text.secondary"
        sx={{ mb: 3, lineHeight: 1.8 }}
      >
        We qualify from after-repair value and project strength—so you close
        faster with fewer personal income conditions. Maximized leverage keeps
        dollars in your renovation and reserve buckets.
      </Typography>

      <Typography variant="h5" sx={{ fontWeight: 800, mb: 2 }}>
        Why our Fix and Flip is Unbeatable
      </Typography>
      <MarketingFeatureGrid
        columns={{ xs: 12, md: 6 }}
        items={[
          {
            title: 'Custom loan structures',
            body: 'Purchase-only, interest-only, and in-progress flips matched to your timeline.',
          },
          {
            title: 'Skip the real estate agent',
            body: 'Submit and negotiate without buyer-agent fees where you choose to.',
          },
          {
            title: 'Custom support',
            body: 'Operators who speak rehab and ROI—not call-center scripts.',
          },
          {
            title: 'Fast approvals',
            body: 'Built for investors who need a yes or no quickly.',
          },
          {
            title: 'No cap on projects underway',
            body: 'Scale rehabs without arbitrary portfolio caps on eligible programs.',
          },
          {
            title: 'True no-income loans',
            body: 'Underwriting centered on the deal, not pay stubs, on approved programs.',
          },
        ]}
      />

      <MarketingCtaBand
        title="Explore Fix and Flip estimates"
        subtitle="Model the deal, then submit offers from the platform."
        href="https://app.unbeatableloans.com/"
        buttonLabel="Open the app"
        external
      />
    </MarketingPageShell>
  );
}
